import type { TimeRange } from "@heraion/shared";

import type { SessionResultsSnapshot } from "./types";
import { RESULTS_PAGE_SIZE } from "./useSessionResults";

/** #397: the disk copy of Progress's newest rows, for a panel that reads a
 *  range of its own (the tracker), only when that copy holds the whole range:
 *  fewer rows than one page (the whole history), or a row older than the
 *  range's start. Else null: a chart with its start missing would lie.
 *  RESULTS_PAGE_SIZE is the size of that copy: fewer rows than it, the whole history. */
export const staleCovering =
  (
    readStale: (() => Promise<SessionResultsSnapshot | null>) | undefined,
    range: TimeRange,
  ): (() => Promise<SessionResultsSnapshot | null>) | undefined =>
  readStale &&
  (async () => {
    const stale = await readStale();
    if (stale === null) return null;
    const { results } = stale;
    // #469: what the server sent, not what passed the schema: a page with a
    // dropped row is still a full page. A copy from before it has no count.
    const sent = stale.rawCount ?? results.length;
    const whole =
      sent < RESULTS_PAGE_SIZE ||
      results.some((r) => r.startedAtMs <= range.fromMs);
    if (!whole) return null;
    return {
      ...stale,
      results: results.filter(
        (r) => r.startedAtMs >= range.fromMs && r.startedAtMs <= range.toMs,
      ),
    };
  });
